import { Controller } from "@hotwired/stimulus"
import { computePosition, autoUpdate, offset, flip, shift, arrow } from "@floating-ui/dom"

export default class extends Controller {
  static targets = ["card", "content", "arrow"];
  static values = {
    url: String,
    portalId: { type: String, default: 'hovercard-portal' }
  };

  cleanup = null;

  connect() {
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.boundHandleDocumentClick);
    this.placement = this.element.dataset.placement || 'bottom'
    this.offset = parseInt(this.element.dataset.offset || 8)

    let existingPortal = document.querySelector(`[data-portal="${this.portalIdValue}"]`);
    if (existingPortal) {
      this.portalElement = existingPortal;
    } else {
      this.portalElement = document.createElement('div');
      this.portalElement.setAttribute('data-portal', this.portalIdValue);
      this.portalElement.style.position = 'relative';
      document.body.appendChild(this.portalElement);

    }
    this.hide()

    if (this.hasCardTarget) {
      this.cardReference = this.cardTarget;
      this.moveCardToPortal();
    }

    // Register this controller in the global registry for child hovercards to find
    if (!window.hovercardRegistry) {
      window.hovercardRegistry = new Map();
    }

    // Generate a unique ID if not present
    this.hovercardId = this.element.id || `hovercard-${Math.random().toString(36).substring(2, 15)}`;
    if (!this.element.id) {
      this.element.id = this.hovercardId;
    }

    // Store reference to parent hovercard if we're inside one
    this.parentHovercard = this.findParentHovercard();

    // Register this hovercard
    window.hovercardRegistry.set(this.hovercardId, this);

  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleDocumentClick);

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    if (this.cardReference && this.cardReference.parentElement === this.portalElement) {
      this.element.appendChild(this.cardReference);
    }

    // Remove from registry
    if (window.hovercardRegistry) {
      window.hovercardRegistry.delete(this.hovercardId);
    }
  }

  findParentHovercard() {
    // Find the parent hovercard controller if we're inside one
    let parent = this.element.closest('[data-hovercard-target="content"]');
    if (parent) {
      const card = parent.closest('[data-hovercard-target="card"]');
      if (card && card.dataset.hovercardId) {
        return window.hovercardRegistry.get(card.dataset.hovercardId);
      }
    }
    return null;
  }

  show(event) {
   if (event) {
     event.stopPropagation();
   }

   // Store the button's bounding rectangle for accurate positioning
   this.buttonRect = this.element.getBoundingClientRect();

   // If we have a parent, close sibling hovercards at our level
   if (this.parentHovercard) {
     this.closeSiblingHovercards();
   } else {
     // If we're a top-level hovercard, just close our children
     this.closeChildHovercards();
   }

   if (this.cardReference) {
     this.cardReference.classList.remove("d-none");
     this.setupNestedHovercards();
     this.updatePosition();
   } else if (this.hasCardTarget) {
     this.cardReference = this.cardTarget;
     this.moveCardToPortal();
     this.cardReference.classList.remove("d-none");
     this.setupNestedHovercards();
     this.updatePosition();
   } else {
     if(this.urlValue) {
       this.isLoading = true; // Add this flag
       this.fetchAndAppendCard().then(() => {
         this.isLoading = false; // Clear the flag when done
       });
     } else {
       this.updateContent(this.content);
     }
   }
  }

  closeSiblingHovercards() {
    // If we have a parent, close all its child hovercards except this one
    if (this.parentHovercard && this.parentHovercard.cardReference) {
      const siblingButtons = this.parentHovercard.cardReference.querySelectorAll('[data-controller="hovercard"]');
      siblingButtons.forEach(button => {
        if (button.id !== this.hovercardId) {
          const controller = this.application.getControllerForElementAndIdentifier(button, 'hovercard');
          if (controller && controller.cardReference && !controller.cardReference.classList.contains('d-none')) {
            controller.hide();
          }
        }
      });
    }
  }

  setupNestedHovercards() {
    // Mark this card so we can identify it in document click handler
    if (this.cardReference) {
      this.cardReference.setAttribute('data-hovercard-id', this.hovercardId);

      // Add a click handler to the content to close child hovercards when parent is clicked
      const contentEl = this.cardReference.querySelector('[data-hovercard-target="content"]');
      if (contentEl) {
        contentEl.addEventListener('click', (event) => {
          // Mark the event as processed by this hovercard to prevent parent hovercards from closing
          event.hoverCardProcessed = true;

          // Close any child hovercards if clicking directly on the content (not on buttons or controls)
          if (event.target === contentEl ||
              (!event.target.closest('[data-controller="hovercard"]') &&
               !event.target.closest('.accordion-button'))) {
            this.closeChildHovercards();
          }
        });
      }
    }
  }

  closeChildHovercards() {
    // Find all visible hovercards that might be children of this one
    if (this.cardReference) {
      const nestedHovercardButtons = this.cardReference.querySelectorAll('[data-controller="hovercard"]');
      nestedHovercardButtons.forEach(button => {
        const controller = this.application.getControllerForElementAndIdentifier(button, 'hovercard');
        if (controller && controller.cardReference && !controller.cardReference.classList.contains('d-none')) {
          controller.hide();
        }
      });
    }
  }

  hide(event) {
    if (event && event.hoverCardProcessed) {
      // Don't hide if the event was processed by a child hovercard
      return;
    }

    // Close any child hovercards when hiding this one
    this.closeChildHovercards();

    if (this.cardReference) {
      this.cardReference.classList.add("d-none");
      if(this.hasCardTarget) { this.cardTarget.classList.add("d-none"); }
    }

    if (this.cleanup) {
      this.cleanup();
    }

    if(event) { event.stopPropagation(); }
  }

  moveCardToPortal() {
    if (this.cardReference && this.portalElement) {
      if (this.cardReference.parentElement !== this.portalElement) {
        this.portalElement.appendChild(this.cardReference);
      }
    }
  }

  toggle(event) {
    if (this.isLoading) return;

    if (this.cardReference && !this.cardReference.classList.contains("d-none")) {
      this.hide(event);
    } else {
      this.show(event);
    }
  }

  async fetchAndAppendCard() {
    try {
      const response = await fetch(this.urlValue)
      const html = await response.text()
      this.updateContent(html)
    } catch (error) {
      console.error("Error fetching hovercard content:", error)
    }
  }

  updateContent(html) {
    if (!this.cardReference && !this.hasCardTarget) {
      const fragment = document.createRange().createContextualFragment(html);
      this.element.appendChild(fragment);

      // Now that we've appended it, store the reference
      if (this.hasCardTarget) {
        this.cardReference = this.cardTarget;
        this.moveCardToPortal();
        this.setupNestedHovercards();
      }
    } else if (this.cardReference) {
      // Find the content target inside the card reference
      const contentElement = this.cardReference.querySelector('[data-hovercard-target="content"]');
      if (contentElement) {
        contentElement.innerHTML = html;
        // Setup any new nested hovercards after content update
        this.setupNestedHovercards();
      }
    }

    this.updatePosition()
  }

  updatePosition() {
    if (this.cleanup) {
      this.cleanup();
    }

    if (!this.cardReference) return;

    // Create a virtual element at the button's position
    const virtualReference = {
      getBoundingClientRect: () => {
        return this.buttonRect || this.element.getBoundingClientRect();
      },
      contextElement: this.element,
    };

    const update = () => {
      computePosition(virtualReference, this.cardReference, {
        placement: this.placement,
        middleware: [
          offset(this.offset),
          flip(),
          shift({ padding: 5 }),
          this.hasArrowTarget ? arrow({ element: this.arrowTarget }) : null
        ].filter(Boolean),
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(this.cardReference.style, {
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          zIndex: 1300
        });

        if (this.hasArrowTarget && middlewareData.arrow) {
          const { x: arrowX, y: arrowY } = middlewareData.arrow;

          const staticSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[placement.split('-')[0]];

          Object.assign(this.arrowTarget.style, {
            position: 'absolute',
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            right: '',
            bottom: '',
            zIndex: '-1',
            [staticSide]: '-4px',
          });
        }
      });
    };

    this.cleanup = autoUpdate(
      this.element,
      this.cardReference,
      update,
      { ancestorScroll: true, ancestorResize: true, elementResize: true }
    );

    // Also update on accordion toggle since this can change the card size
    const accordionButtons = this.cardReference.querySelectorAll('.accordion-button');
    accordionButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Give the accordion time to expand/collapse
        setTimeout(update, 300);
      });
    });
  }

  handleDocumentClick(event) {
    // Don't process if card is already hidden
    if (!this.cardReference || this.cardReference.classList.contains("d-none")) {
      return;
    }

    // Check if this click is inside any hovercard
    let isInAnyHovercard = event.target.closest('[data-hovercard-target="card"]');

    // Check if the click is outside both the trigger and the card
    if (!isInAnyHovercard && !this.element.contains(event.target)) {
      this.hide();
    }
  }

  handleScroll() {
    if (this.cardReference && !this.cardReference.classList.contains("d-none")) {
      this.buttonRect = this.element.getBoundingClientRect();
      this.updatePosition();
    }
  }
}
